// Constants
import { FilterTypes } from "../constants/FilterConstants";
// Models
import { DayOfWeek, RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
import { Temporal } from "temporal-polyfill-lite";
import { ContentType } from "../client/planner/common/IItemBody";
import { CustomFieldValue } from "../client/planner/customFields/CustomFieldValue";
import { RecurrenceDropdownType } from "../client/planner/recurrence/RecurrenceDropdownType";
import { RecurrenceOccurrence } from "../client/planner/recurrence/RecurrenceOccurrence";
import { RecurrencePattern } from "../client/planner/recurrence/RecurrencePattern";
import { RecurrenceRange } from "../client/planner/recurrence/RecurrenceRange";
import { RecurrenceSchedule } from "../client/planner/recurrence/RecurrenceSchedule";
import { TaskPreviewType } from "../client/planner/task/ITask";
import { Task } from "../client/planner/task/Task";
import { TaskPersonalViewOrderHints } from "../client/planner/task/TaskPersonalViewOrderHints";
import { TaskPlanViewOrderHints } from "../client/planner/task/TaskPlanViewOrderHints";
import { FilterFields } from "../constants/FilterConstants";
import { RecurrenceEndType, RecurrencePatternType } from "../service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { getDayOfWeekFromIndex, getWeekIndex } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
/**
 * Returns a map of categories that are in use by at least one task or has been renamed by the user
 * @param tasks Task data
 * @param categories Categories from the Plan data. If value is a string, it is a custom label name. If null, it is the default label name.
 * @returns Map where the key is the category index, and value of true means the category is in use
 */ export function getUsedCategoriesMap(tasks, categories) {
    // Determine subset of labels that are used by at least one task
    const usedCategories = tasks.reduce((_usedCategories, currentTask)=>{
        // In the new planner model, applied category IDs are in planView.appliedCategoryIds
        currentTask.planView.appliedCategoryIds.forEach((categoryId)=>{
            // Convert categoryId to numeric index if it's a number string
            const categoryIndex = parseInt(categoryId, 10);
            // If categoryIndex is a valid number, mark it as used
            if (!isNaN(categoryIndex)) {
                _usedCategories[categoryIndex] = true;
            }
        });
        return _usedCategories;
    }, {});
    // Also include categories that have been renamed by the user
    categories.forEach((categoryName, category)=>{
        if (categoryName !== null && categoryName.length > 0) {
            usedCategories[category] = true;
        }
    });
    return usedCategories;
}
/**
 * Returns a map of user IDs that are assigned to at least one task
 * @param tasks Task data
 * @returns Map where the key is the user ID, and value of true means user has at least one assignment
 */ export function getTaskAssigneeIdsMap(tasks) {
    const userIdsWithAssignments = tasks.reduce((_userIdsWithAssignments, currentTask)=>{
        // In the new planner model, user assignments are in the userAssignments array
        currentTask.userAssignments.forEach((assignment)=>{
            _userIdsWithAssignments[assignment.user.id] = true;
        });
        return _userIdsWithAssignments;
    }, {});
    return userIdsWithAssignments;
}
/**
 * Returns all the referenced user ids for the task
 * @param task Task for which the referenced users are needed
 * @returns an array of referenced user ids
 */ export function getTaskReferencedUserIds(task) {
    const assignments = task.userAssignments.map((userAssignment)=>userAssignment.user.id);
    const createdByUser = task.creationInfo.createdBy.user;
    // if createdBy user is not already a part of assignments and is not empty
    if (createdByUser.id && assignments.indexOf(createdByUser.id) === -1) {
        assignments.push(task.creationInfo.createdBy.user.id);
    }
    return assignments;
}
/**
 * Filters the tasks based on unique equivalence set ids for all project tasks
 * and returns 1 project task per equivalence set id
 * @param tasks tasks to be filtered
 */ export function filterUniqueTasksBasedOnEquivalenceSetIds(tasks) {
    const equivalenceSetIdToTaskMapping = {};
    const filteredTasks = [];
    for (const task of tasks){
        // planId is a placeholder, should be replaced with new field for equivalence set id on task
        const equivalenceSetId = task.planId;
        // In new planner model, project task information might be stored differently
        // Using conversationThreadId as a placeholder to determine if it's a project task
        const isProjectTask = Boolean(task.extensions.conversationThreadId);
        // If it's a project task and not already mapped, add it to the filtered tasks
        if (isProjectTask && !equivalenceSetIdToTaskMapping[equivalenceSetId]) {
            equivalenceSetIdToTaskMapping[equivalenceSetId] = task;
            filteredTasks.push(task);
        }
    }
    return filteredTasks;
}
/**
 * Determines whether or not an update contains a checklist item reorder
 * @param updatedChecklistItems Record of updated checklist items
 * @param originalChecklistItems The original checklist items array (unified model)
 */ export function updateContainsChecklistItemReorder(updatedChecklistItems, originalChecklistItems) {
    // Check for reorder by comparing orderHint values in extensions.LegacyData
    // In unified model, orderHint is stored in extensions.LegacyData.orderHint
    return Object.values(updatedChecklistItems).some((checklistItem)=>{
        const originalChecklistItem = originalChecklistItems.find((item)=>item.id === checklistItem.id);
        // If the original checklist item is not found, we can't determine reorder
        if (!originalChecklistItem) {
            return false;
        }
        // Extract orderHint from unified model extensions.LegacyData.orderHint
        const getOrderHint = (item)=>{
            return item.extensions.orderHint;
        };
        const originalOrderHint = getOrderHint(originalChecklistItem);
        const updatedOrderHint = getOrderHint(checklistItem);
        // If both have orderHint values and they're different, it's a reorder
        return Boolean(originalOrderHint && updatedOrderHint && originalOrderHint !== updatedOrderHint);
    });
}
/**
 * Create a temporary task
 * @param taskId Id of the temp task to create
 * @param planId Id of the plan
 * @param taskKind The kind of task to create
 * @param goalId Optional goal ID to associate with the task
 * @param name Optional name of the task
 */ export function createTempTask(taskId, planId, taskKind, goalId, name, overrides) {
    // Create a new task with required properties for the new planner model
    const now = Temporal.Now.instant();
    const tempTask = new Task({
        id: taskId,
        version: "1",
        archivalInfo: {
            isArchived: false,
            justification: null,
            modifiedBy: null,
            modifiedDate: null
        },
        capabilities: {
            entityExtensions: false
        },
        userAssignments: [],
        bucketId: null,
        checklist: [],
        creationInfo: {
            createdBy: {
                user: {
                    id: "",
                    tenantId: ""
                },
                application: {
                    id: "",
                    tenantId: ""
                }
            },
            createdDateTime: now,
            externalId: null,
            externalVersion: null,
            scenarioId: null,
            scope: null,
            externalUrl: null
        },
        dependencies: [],
        displayName: name ?? "",
        dueDateTime: overrides?.dueDateTime ?? null,
        extensions: {
            conversationThreadId: null
        },
        finishDateTime: null,
        goalIds: goalId ? [
            goalId
        ] : [],
        hasChildren: false,
        kind: taskKind,
        lastModifiedBy: {
            user: {
                id: "",
                tenantId: ""
            },
            application: {
                id: "",
                tenantId: ""
            }
        },
        lastModifiedDateTime: now,
        links: [],
        notes: {
            content: "",
            contentType: ContentType.HTML
        },
        parentTaskId: null,
        permissionSetId: "",
        planId: planId,
        priority: overrides?.priority ?? 5,
        personalView: {
            isOnMyDay: false,
            orderHints: new TaskPersonalViewOrderHints()
        },
        planView: {
            appliedCategoryIds: [],
            previewType: TaskPreviewType.NoPreview,
            orderHints: new TaskPlanViewOrderHints()
        },
        startDateTime: null,
        percentComplete: overrides?.percentComplete ?? 0,
        completedBy: null,
        completedDateTime: null
    });
    return tempTask;
}
/**
 * Returns a list of users that have task assignments, or if the filter option for that user is applied
 * @param users Users data
 * @param tasks Tasks data
 * @param taskFilter Current task filter
 */ export function getUsersForAssignmentFilterOptions(users, tasks, taskFilter) {
    // Determine set of user IDs who have task assignments
    const userIdsWithAssignments = getTaskAssigneeIdsMap(tasks);
    // Only render user assignment filter options for users that have task assignments,
    // or if the filter option is already applied.
    const usersToRender = users.filter((user)=>userIdsWithAssignments[user.id] || taskFilter.doesFilterValueExist(FilterFields[FilterTypes.UserAssignments], user.id));
    return usersToRender;
}
/**
 * Returns a list of categories that are in use, or if the filter option for that category is applied
 * @param categories Categories from the Plan data. If value is a string, it is a custom label name. If null, it is the default label name.
 * @param tasks Tasks data
 * @param taskFilter Current task filter
 */ export function getCategoryFilterOptions(categories, tasks, taskFilter) {
    let categoryFilterOptions = categories.map((categoryName, categoryNumber)=>({
            categoryNumber,
            categoryName
        }));
    if (tasks != null) {
        // Determine labels/categories that are used by at least one task
        const usedCategories = getUsedCategoriesMap(tasks, categories);
        // Only render category filter options that are used by at least one task,
        // or if the category filter is already applied.
        categoryFilterOptions = categoryFilterOptions.filter(({ categoryNumber })=>usedCategories[categoryNumber] || taskFilter.doesFilterValueExist(FilterFields[FilterTypes.AppliedCategoryIds], categoryNumber.toString()));
    }
    return categoryFilterOptions;
}
/**
 * Create a temporary recurrence occurrence for a task using the schedule or defaulting to a temporary one
 * @param task Task to create the recurrence occurrence for
 * @param schedule Optional recurrence schedule to use for the occurrence
 * @see createRecurrenceSchedule
 */ export function createTempRecurrenceOccurrence(task, schedule) {
    return new RecurrenceOccurrence({
        seriesId: "temp-series-id-for-" + task.id,
        occurrenceId: 1,
        previousInSeriesTaskId: null,
        nextInSeriesTaskId: null,
        recurrenceStartDateTime: Temporal.Now.instant(),
        // Default to a temp recurrence schedule if none provided
        schedule: schedule ?? createRecurrenceSchedule()
    });
}
/**
 * Create a recurrence schedule using optional pattern with default range of EndOfPlan type starting today
 * @param pattern Optional recurrence pattern for the recurrence schedule, otherwise defaults to daily pattern
 */ export function createRecurrenceSchedule(pattern) {
    return new RecurrenceSchedule({
        pattern: pattern ?? // Default to daily pattern if none provided
        RecurrencePattern.fromResource({
            dayOfMonth: 0,
            daysOfWeek: [],
            firstDayOfWeek: DayOfWeek.Sunday,
            index: RelativeWeekIndex.First,
            interval: 1,
            month: 0,
            type: RecurrencePatternType.Daily
        }),
        range: RecurrenceRange.fromResource({
            startDate: Temporal.Now.instant().toString(),
            recurrenceEndType: RecurrenceEndType.EndOfPlan,
            endDate: null,
            countOfInstances: null
        }),
        nextOccurrenceDateTime: null
    });
}
const WEEKDAY_PATTERN_DAYS = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday
];
const DEFAULT_FIRST_DAY_OF_WEEK = DayOfWeek.Sunday; // TODO Task 2786922: Get locale specific first day of week
function resolveDaysOfWeek(options) {
    if (options?.daysOfWeek && options.daysOfWeek.length > 0) {
        return options.daysOfWeek;
    }
    if (options?.patternDate) {
        return [
            getDayOfWeekFromIndex(options.patternDate.temporal)
        ];
    }
    return [
        DayOfWeek.Monday
    ];
}
function resolveWeekIndex(options) {
    if (options?.weekIndex != null) {
        return options.weekIndex;
    }
    if (options?.patternDate) {
        return getWeekIndex(options.patternDate.date);
    }
    return RelativeWeekIndex.First;
}
/**
 * Create a recurrence pattern from a dropdown type with pattern date used for certain patterns
 * @param recurrenceDropdownType Dropdown type to create the pattern from
 * @param options Optional metadata to drive the resulting recurrence pattern
 */ export function createRecurrencePatternFromDropdownType(recurrenceDropdownType, options) {
    const interval = options?.interval && !isNaN(options.interval) && options.interval > 0 ? options.interval : 1;
    const daysOfWeek = resolveDaysOfWeek(options);
    const dayOfMonth = options?.dayOfMonth ?? options?.patternDate?.date.day ?? 1;
    const month = options?.month ?? options?.patternDate?.date.month ?? 1;
    const weekIndex = resolveWeekIndex(options);
    const isAbsolute = options?.isAbsolute ?? true;
    switch(recurrenceDropdownType){
        case RecurrenceDropdownType.Weekdays:
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Weekly,
                interval: 1,
                daysOfWeek: WEEKDAY_PATTERN_DAYS,
                firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                dayOfMonth: null,
                dayOfWeek: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.Weekly:
            if (interval === 1) {
                // All 7 days selected is equivalent to daily recurrence
                if (daysOfWeek.length === 7) {
                    return new RecurrencePattern({
                        patternType: RecurrencePatternType.Daily,
                        interval: 1,
                        dayOfMonth: null,
                        dayOfWeek: null,
                        daysOfWeek: null,
                        firstDayOfWeek: null,
                        month: null,
                        weekIndex: null
                    });
                }
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.Weekly,
                    interval: 1,
                    dayOfWeek: null,
                    daysOfWeek: daysOfWeek,
                    firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                    dayOfMonth: null,
                    month: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Weekly,
                interval,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                dayOfMonth: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.Monthly:
            if (isAbsolute) {
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfMonth,
                    interval,
                    dayOfMonth,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    month: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.RelativeDayOfMonth,
                interval,
                dayOfMonth: null,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: null,
                month: null,
                weekIndex
            });
        case RecurrenceDropdownType.Yearly:
            if (isAbsolute) {
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfYear,
                    interval,
                    dayOfMonth,
                    month,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.RelativeDayOfYear,
                interval,
                dayOfMonth: null,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: null,
                month,
                weekIndex
            });
        case RecurrenceDropdownType.Daily:
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Daily,
                interval,
                dayOfMonth: null,
                dayOfWeek: null,
                daysOfWeek: null,
                firstDayOfWeek: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.NotRepeat:
        case RecurrenceDropdownType.Custom:
        default:
            return null;
    }
}
/**
 * Get checklist items sorted by order hint in reversed order
 * @param checklistItems Array of checklist items to sort
 * @returns Sorted array of checklist items
 */ function getSortedChecklistItems(checklistItems) {
    return checklistItems.slice().sort((item1, item2)=>{
        return -1 * compareOrderHints(item1.getOrderHint(), item2.getOrderHint());
    });
}
/**
 * Create a copy of the checklist with correct order hints
 * @param checklist Checklist items to create a copy of
 * @returns New checklist items array with correct order hints
 */ export function createChecklistCopyWithCorrectOrderHint(checklist) {
    const asciiValueOfZ = "Z".charCodeAt(0);
    if (checklist) {
        // There is a max of 20 checklist items and reversed ordered after getSortedChecklistItems, if we start the ascii value of the 'Z', we can go up to 'G'
        const sortedItems = getSortedChecklistItems(checklist);
        const checklistArray = sortedItems.map((item, index)=>{
            const tempOrderHint = String.fromCharCode(asciiValueOfZ - index);
            return item.setOrderHint(tempOrderHint);
        });
        const finalArray = checklistArray.map((item, index)=>{
            let newOrderHint;
            if (index === 0) {
                newOrderHint = generateOrderHintBetween(item.getOrderHint(), null);
            } else {
                newOrderHint = generateOrderHintBetween(item.getOrderHint(), checklistArray[index - 1].getOrderHint());
            }
            return item.setOrderHint(newOrderHint);
        });
        return finalArray;
    }
}
/**
 * Sort by preview priority in reversed order
 * @param contentLinks Array of content links to sort
 * @param links sorted array of content links
 */ function getSortedLinks(contentLinks) {
    return contentLinks.slice().sort((item1, item2)=>{
        return -1 * compareOrderHints(item1.extensions.previewPriority, item2.extensions.previewPriority);
    });
}
/**
 * Create a copy of the custom field values with new entity IDs
 * @param customFieldValues Custom field values to create a copy of
 * @returns New custom field values array with fresh IDs preserving field bindings and values
 */ export function createCustomFieldValuesCopy(customFieldValues) {
    return customFieldValues.map((fieldValue)=>new CustomFieldValue({
            id: LocalIdGenerator.generate(),
            version: "1",
            customFieldDefinitionId: fieldValue.customFieldDefinitionId,
            value: fieldValue.value
        }));
}
/**
 * Create a copy of the content links with correct priority order
 * @param contentLinks Content links to create a copy of
 * @returns New content links array with correct order hints
 */ export function createContentLinkCopyWithCorrectPriorityOrder(contentLinks) {
    const asciiValueOfZ = "Z".charCodeAt(0);
    if (contentLinks) {
        // There is a max of 10 reference items and reversed ordered after getSortedLinks, if we start the ascii value of the 'Z', we can go up to 'Q'
        const sortedItems = getSortedLinks(contentLinks);
        const linksList = sortedItems.map((item, index)=>{
            const tempPreviewPriority = String.fromCharCode(asciiValueOfZ - index);
            return item.setProperty("extensions", {
                ...item.extensions,
                previewPriority: tempPreviewPriority
            });
        });
        const finalArray = linksList.map((item, index)=>{
            let newPreviewPriority;
            if (index === 0) {
                newPreviewPriority = generateOrderHintBetween(linksList[index].extensions.previewPriority, null);
            } else {
                newPreviewPriority = generateOrderHintBetween(linksList[index].extensions.previewPriority, linksList[index - 1].extensions.previewPriority);
            }
            return item.setProperty("extensions", {
                ...item.extensions,
                previewPriority: newPreviewPriority
            });
        });
        return finalArray;
    }
}
